type EventData = {
  greeting: GreetingData;
};

export type CustomGreetingEvent = CustomEvent<EventData>;

export interface GreetingData {
  title?: string;
  message?: string;
  icon?: string;
}

export type CustomGreetingPayload = {
  greeting: GreetingData;
};

class CustomGreetingElement extends HTMLElement {
  private greeting: GreetingData = {};

  public dfPayload: string | null = null;
  public dfResponseId: string | null = null;

  constructor() {
    super();

    // Initialize properties which df-cx-messenger will set
    this.dfPayload = null;
    this.dfResponseId = null;

    this.attachShadow({ mode: 'open' });
  }

  /**
   * Called when the element is inserted into the DOM
   */
  connectedCallback() {
    try {
      // Get greeting data from the element's payload attribute
      const payloadAttr = this.dfPayload;
      if (payloadAttr) {
        const payload = JSON.parse(payloadAttr) as CustomGreetingPayload;
        this.greeting = payload.greeting || {};
        this.render();
      }
    } catch (error) {
      // Silent error handling
    }
  }

  /**
   * Render the greeting UI
   */
  private render() {
    if (!this.shadowRoot) return;

    const style = document.createElement('style');
    style.textContent = `
      :host {
        /* This targets the <df-custom-greeting> element itself */
        /* Base styling - Compact */
        --df-greeting-font-family: inherit;
        --df-greeting-font-size-title: 14px;
        --df-greeting-font-size-message: 13px;
        
        /* Container styling - Compact */
        --df-greeting-container-padding: 10px 12px;
        --df-greeting-container-margin: 6px 0;
        --df-greeting-container-background: #f8f9fa;
        --df-greeting-container-border-radius: 8px;
        --df-greeting-container-border: 1px solid #e8eaed;
        --df-greeting-container-box-shadow: 0 1px 3px rgba(0,0,0,0.08);
        
        /* Title styling - Compact */
        --df-greeting-title-color: #1967d2;
        --df-greeting-title-margin: 0 0 8px 0;
        --df-greeting-title-font-weight: 600;
        
        /* Message styling - Compact */
        --df-greeting-message-color: #4a4a4a;
        --df-greeting-message-margin: 0;
        --df-greeting-message-line-height: 1.4;
        
        /* Icon styling - Compact */
        --df-greeting-icon-size: 20px;
        --df-greeting-icon-margin: 0 8px 0 0;
        
        /* Animation */
        --df-greeting-animation-duration: 0.3s;
      }

      .greeting-container {
        padding: var(--df-greeting-container-padding);
        margin: var(--df-greeting-container-margin);
        background-color: var(--df-greeting-container-background);
        border-radius: var(--df-greeting-container-border-radius);
        border: var(--df-greeting-container-border);
        box-shadow: var(--df-greeting-container-box-shadow);
        font-family: var(--df-greeting-font-family);
        animation: fade-in var(--df-greeting-animation-duration) ease-in-out;
      }
      
      @keyframes fade-in {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      .greeting-header {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
      }
      
      .greeting-icon {
        width: var(--df-greeting-icon-size);
        height: var(--df-greeting-icon-size);
        margin: var(--df-greeting-icon-margin);
        flex-shrink: 0;
      }
      
      .greeting-icon svg {
        width: 100%;
        height: 100%;
      }
      
      .greeting-title {
        font-weight: var(--df-greeting-title-font-weight);
        margin: var(--df-greeting-title-margin);
        font-size: var(--df-greeting-font-size-title);
        color: var(--df-greeting-title-color);
      }
      
      .greeting-message {
        margin: var(--df-greeting-message-margin);
        font-size: var(--df-greeting-font-size-message);
        color: var(--df-greeting-message-color);
        line-height: var(--df-greeting-message-line-height);
      }
    `;

    const container = document.createElement('div');
    container.className = 'greeting-container';

    // Header section with icon and title
    const header = document.createElement('div');
    header.className = 'greeting-header';

    // Add icon if provided
    if (this.greeting.icon) {
      const iconWrapper = document.createElement('div');
      iconWrapper.className = 'greeting-icon';
      iconWrapper.innerHTML = this.greeting.icon;
      header.appendChild(iconWrapper);
    } else {
      // Default icon (a simple wave hand emoji as SVG)
      const iconWrapper = document.createElement('div');
      iconWrapper.className = 'greeting-icon';
      iconWrapper.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1967d2">
          <path d="M12,2C6.486,2,2,6.486,2,12s4.486,10,10,10s10-4.486,10-10S17.514,2,12,2z M12,20c-4.411,0-8-3.589-8-8 s3.589-8,8-8s8,3.589,8,8S16.411,20,12,20z"/>
          <path d="M13,7h-2v5.414l3.293,3.293l1.414-1.414L13,11.586V7z"/>
        </svg>
      `;
      header.appendChild(iconWrapper);
    }

    // Add title
    const title = document.createElement('div');
    title.className = 'greeting-title';
    title.textContent = this.greeting.title || 'Welcome!';
    header.appendChild(title);

    container.appendChild(header);

    // Add message
    if (this.greeting.message) {
      const message = document.createElement('div');
      message.className = 'greeting-message';
      message.textContent = this.greeting.message;
      container.appendChild(message);
    }

    // Clear any existing content
    while (this.shadowRoot.firstChild) {
      this.shadowRoot.removeChild(this.shadowRoot.firstChild);
    }

    // Add the style and container to the shadow root
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(container);

    // Dispatch event that greeting was rendered
    this.dispatchEvent(
      new CustomEvent('df-custom-greeting-rendered', {
        bubbles: true,
        composed: true,
      })
    );
  }
}

// Register the custom element
if (!customElements.get('df-custom-greeting')) {
  customElements.define('df-custom-greeting', CustomGreetingElement);
}
